import React, { useEffect, useState } from 'react';
import { cn } from 'lib/utils';
import Icon from 'components/ui/icon';

interface RefreshButtonProps {
  /** Callback function when refresh is clicked */
  onRefresh: () => void;
  /** Optional active tab identifier - resets timer when changed */
  activeTab?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Reusable refresh button component with "last updated" timestamp.
 * Displays time since last update and allows manual refresh.
 */
const RefreshButton: React.FC<RefreshButtonProps> = ({
  onRefresh,
  activeTab,
  className,
}) => {
  const [timer, setTimer] = useState(0);
  const [lastUpdateMessage, setLastUpdateMessage] = useState(
    'Last updated a few seconds ago'
  );

  // Tick once per second. Stable interval — created on mount, cleared on unmount.
  useEffect(() => {
    const timerInterval = setInterval(() => {
      setTimer((prevTimer) => prevTimer + 1);
    }, 1000);

    return () => clearInterval(timerInterval);
  }, []);

  // Refresh the "last updated" label as the elapsed timer advances.
  useEffect(() => {
    const second = timer < 60 ? timer : 0;
    const minute = timer >= 60 && timer <= 3540 ? Math.floor(timer / 60) : 0;
    if (second !== 0) {
      setLastUpdateMessage('Last updated a few seconds ago');
    } else if (minute) {
      setLastUpdateMessage(
        `Last updated ${minute} minute${minute > 1 ? 's' : ''} ago`
      );
    } else if (timer > 3540) {
      setLastUpdateMessage('Last updated over an hour ago');
    }
  }, [timer]);

  useEffect(() => {
    setTimer(0);
  }, [activeTab]);

  const handleRefresh = () => {
    onRefresh();
    setTimer(0);
  };

  return (
    <div className={cn('flex items-center ml-4', className)}>
      <div className="text-muted-foreground flex items-center">
        <Icon name="clock" size={16} />
        <span className="ml-1 mr-10 text-xs font-normal leading-4">
          {lastUpdateMessage}
        </span>
      </div>
      <div
        className="flex items-center gap-1 text-xs font-normal leading-4 text-dark-green-link hover:opacity-80 cursor-pointer"
        onClick={handleRefresh}
      >
        <Icon name="refresh" size={16} />
        <span>Refresh</span>
      </div>
    </div>
  );
};

export default RefreshButton;
